
import { v4 as uuidv4 } from 'uuid';
import { Medication } from '@/types/medication';
import { databaseService } from './DatabaseService';
import { MedicationTransformService } from './MedicationTransformService';
import { useMedicationStore } from '@/store/medicationStore';
import { MEDICATION_DB_PREFIX, OperationPriority } from '@/constants/databaseConstants';
import { loggingService } from './LoggingService';
import { DatabaseOperationOptions } from './DatabaseService/types';

export class MedicationService {
  static async saveMedication(rawData: any): Promise<void> {
    try {
      // Log detailed information for cycle-based medications to help with debugging
      if (rawData.category === 'cycle-based' || rawData.cycle) {
        loggingService.info('database', `Saving cycle-based medication: ${rawData.id} ${rawData.name || rawData.medicationName}`);
        loggingService.debug('database', JSON.stringify({ 
          id: rawData.id, 
          name: rawData.name || rawData.medicationName,
          cycle: rawData.cycle,
          cycleLength: typeof rawData.cycleLength === 'string' ? Number(rawData.cycleLength) : rawData.cycleLength,
          activeLength: typeof rawData.activeLength === 'string' ? Number(rawData.activeLength) : rawData.activeLength,
          inactiveLength: typeof rawData.inactiveLength === 'string' ? Number(rawData.inactiveLength) : rawData.inactiveLength,
          cycleStart: rawData.cycleStart
        }));
      } else {
        loggingService.info('database', `Saving new medication: ${rawData.id} ${rawData.name || rawData.medicationName}`);
      }
      
      // Before standardizing, ensure all cycle-related properties are numbers
      if (rawData.cycleLength) rawData.cycleLength = Number(rawData.cycleLength);
      if (rawData.activeLength) rawData.activeLength = Number(rawData.activeLength);
      if (rawData.inactiveLength) rawData.inactiveLength = Number(rawData.inactiveLength);
      
      const standardizedData = MedicationTransformService.standardizeMedicationData(rawData);
      const medicationId = standardizedData.id || uuidv4();

      // Set a flag in localStorage to indicate medications have been added
      localStorage.setItem("hasMedications", "true");

      // Ensure we use properly capitalized day names throughout the application
      const standardDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      // Get profile-specific state and functions
      const { 
        getMedicationsForCurrentProfile, 
        addMedication, 
        currentProfileId 
      } = useMedicationStore.getState();
      
      // Get current medications for the specific profile
      const currentProfileMedications = getMedicationsForCurrentProfile();
      
      // Create a new object with standardized capitalized day names
      const standardizedMedications: Record<string, Medication[]> = {};
      
      // First, initialize with empty arrays for all standard days
      standardDays.forEach(day => {
        standardizedMedications[day] = [];
      });
      
      // Then copy existing medications with proper capitalization
      Object.entries(currentProfileMedications).forEach(([day, medications]) => {
        // Find the matching standard day (case-insensitive)
        const standardDay = standardDays.find(
          standardDay => standardDay.toLowerCase() === day.toLowerCase()
        );
        
        if (standardDay && medications) {
          // Add existing medications to the proper capitalized day
          standardizedMedications[standardDay] = [...medications];
        }
      });
      
      // Add the new medication to each day of the week
      standardDays.forEach(day => {
        // Create a separate instance of the medication for each day to prevent reference issues
        const medicationForDay = { 
          ...standardizedData,
          // Ensure we have proper IDs and references for this specific day's instance
          id: standardizedData.id,
          day: day
        };
        
        // Log for debugging
        // Removed complex console statement for security;
        
        // Initialize array if it doesn't exist yet
        if (!standardizedMedications[day]) {
          standardizedMedications[day] = [];
        }
        standardizedMedications[day].push(medicationForDay);
      });

      
      
      // Add the medication to the store for the current profile
      // Pass standardized medication data with all necessary cycle information
      addMedication(medicationId, {
        ...standardizedData,
        // Ensure these are proper JavaScript numbers, not strings
        cycleLength: standardizedData.cycleLength ? Number(standardizedData.cycleLength) : undefined,
        activeLength: standardizedData.activeLength ? Number(standardizedData.activeLength) : undefined,
        inactiveLength: standardizedData.inactiveLength ? Number(standardizedData.inactiveLength) : undefined,
      });
      
      // Save to database with the profile ID
      try {
        // Use refactored DatabaseService with document type prefix and operation priority
        const saveOptions: DatabaseOperationOptions = {
          collection: MEDICATION_DB_PREFIX,
          docId: medicationId,
          data: standardizedData,
          profileId: currentProfileId || 'default',
          priority: OperationPriority.HIGH
        };
        
        await databaseService.saveData(saveOptions);
      } catch (error) {
        loggingService.error('database', `Failed to save medication ${medicationId}: ${error instanceof Error ? error.message : String(error)}`);
        // Don't throw here to allow the UI to still update even if database save fails
      }

      // Trigger update event with debounce flag to prevent multiple renders
      const event = new CustomEvent('medicationUpdated', {
        detail: { 
          medications: standardizedMedications,
          isBatchUpdate: true
        }
      });
      document.dispatchEvent(event);
      
      return;
    } catch (error) {
      // Only log error once, and only in development
      if (process.env.NODE_ENV !== 'production') {
        
      }
      throw error;
    }
  }

  static async getMedications(profileId?: string): Promise<Record<string, Medication[]>> {
    try {
      // Get the current profile ID if not provided
      const targetProfileId = profileId || useMedicationStore.getState().currentProfileId;
      
      
      
      // Get data for the specific profile
      let data;
      try {
        const profileIdToUse = targetProfileId || 'default';
        const getOptions: DatabaseOperationOptions = {
          collection: MEDICATION_DB_PREFIX,
          profileId: profileIdToUse,
          priority: OperationPriority.HIGH
        };
        
        data = await databaseService.getData(getOptions);
      } catch (error) {
        // Gracefully handle 404 errors (missing documents)
        if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
          loggingService.info('database', `No medications found for profile, creating empty document`);
          
          // Initialize empty medication data for this profile to prevent future 404s
          const emptyData = {};
          const profileIdToUse = targetProfileId || 'default';
          const saveOptions: DatabaseOperationOptions = {
            collection: MEDICATION_DB_PREFIX,
            docId: `${MEDICATION_DB_PREFIX}_${profileIdToUse}`,
            data: emptyData,
            profileId: profileIdToUse,
            priority: OperationPriority.MEDIUM
          };
          
          await databaseService.saveData(saveOptions);
          return emptyData;
        } else {
          // Log other errors but don't crash
          
          return {};
        }
      }
      
      // Ensure we return the correct type even if the database returns empty or null
      if (!data) {
        
        return {};
      }
      
      // Validate that the result is a Record<string, Medication[]>
      if (typeof data === 'object') {
        return data as Record<string, Medication[]>;
      }
      
      
      return {};
    } catch (error) {
      
      return {}; // Return empty object instead of throwing
    }
  }
}

export const medicationService = new MedicationService();
